var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import { ref, computed, onMounted, onUnmounted, provide, nextTick, watch, } from "vue";
export default /*@__PURE__*/ _defineComponent({
    __name: 'Dropdown',
    props: {
        placement: { type: String, required: false, default: "bottom-start" },
        disabled: { type: Boolean, required: false, default: false }
    },
    emits: ["open", "close"],
    setup(__props, { expose: __expose, emit: __emit }) {
        __expose();
        const props = __props;
        const emit = __emit;
        const isOpen = ref(false);
        const dropdownRef = ref();
        const menuRef = ref();
        const menuPosition = ref({ top: 0, left: 0 });
        const menuClasses = computed(() => [
            "_dropdown-menu",
            `_dropdown-menu--${props.placement}`,
            {
                "_dropdown-menu--open": isOpen.value,
            },
        ]);
        const menuStyle = computed(() => ({
            position: "absolute",
            zIndex: 1000,
            top: `${menuPosition.value.top}px`,
            left: `${menuPosition.value.left}px`,
        }));
        const calculatePosition = () => {
            if (!dropdownRef.value)
                return;
            const rect = dropdownRef.value.getBoundingClientRect();
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
            let top = rect.bottom + scrollTop + 4; // 4px margin
            let left = rect.left + scrollLeft;
            // Adjust based on placement
            if (props.placement.includes("top")) {
                top = rect.top + scrollTop - 4; // Will be adjusted after menu renders
            }
            if (props.placement.includes("end")) {
                left = rect.right + scrollLeft - 160; // Assuming min-width of menu
            }
            menuPosition.value = { top, left };
        };
        const toggleDropdown = () => __awaiter(this, void 0, void 0, function* () {
            if (props.disabled)
                return;
            if (!isOpen.value) {
                calculatePosition();
                isOpen.value = true;
                emit("open");
                // Recalculate after menu renders for accurate positioning
                yield nextTick();
                if (menuRef.value && props.placement.includes("top")) {
                    const menuHeight = menuRef.value.offsetHeight;
                    menuPosition.value.top -= menuHeight + 8; // Adjust for actual menu height
                }
            }
            else {
                isOpen.value = false;
                emit("close");
            }
        });
        const closeDropdown = () => {
            if (isOpen.value) {
                isOpen.value = false;
                emit("close");
            }
        };
        // Provide dropdown context to child components
        provide("dropdownContext", {
            toggle: toggleDropdown,
            close: closeDropdown,
            isOpen: computed(() => isOpen.value),
            disabled: computed(() => props.disabled),
        });
        const handleClickOutside = (event) => {
            if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
                closeDropdown();
            }
        };
        const handleKeydown = (event) => {
            if (event.key === "Escape") {
                closeDropdown();
            }
        };
        // Recalculate position when opened or window resizes/scrolls
        watch(isOpen, (newValue) => {
            if (newValue) {
                nextTick(() => {
                    calculatePosition();
                });
            }
        });
        onMounted(() => {
            document.addEventListener("click", handleClickOutside);
            document.addEventListener("keydown", handleKeydown);
            window.addEventListener("scroll", calculatePosition, true);
            window.addEventListener("resize", calculatePosition);
        });
        onUnmounted(() => {
            document.removeEventListener("click", handleClickOutside);
            document.removeEventListener("keydown", handleKeydown);
            window.removeEventListener("scroll", calculatePosition, true);
            window.removeEventListener("resize", calculatePosition);
        });
        const __returned__ = { props, emit, isOpen, dropdownRef, menuRef, menuPosition, menuClasses, menuStyle, calculatePosition, toggleDropdown, closeDropdown, handleClickOutside, handleKeydown };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
